import styles from "./index.module.scss";
import type { TExpTemp } from "@/types";
import clsx from "clsx";
import {
  PlaySvg,
  PauseSvg,
  VolumeSvg,
  MuteSvg,
  RemoveSvg,
} from "@/assets/icons";
import { useRef, type MouseEventHandler, useEffect, useState } from "react";
import { useCommonStore } from "@/stores";
import FTAnalytic from "@/analyze/FTAnalytic";
import { ExpTempType, PROJECT_TYPE } from "@/constants";
import { Loading } from "@/components";
import { blurImg } from "@/utils";

type ControlButtonProps = {
  style?: object;
  disabled?: boolean;
  children: any;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

const ControlButton = (props: ControlButtonProps) => {
  const {
    style = {},
    disabled,
    children,
    onClick,
  } = props;

  return (
    <button
      style={style}
      className={styles["control_button"]}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

type ExpTempProps = {
  active: boolean;
  isMute?: boolean;
  onClick: MouseEventHandler<HTMLDivElement>;
  onPlay?: MouseEventHandler<HTMLButtonElement>;
  onPause?: MouseEventHandler<HTMLButtonElement>;
  onMute?: MouseEventHandler<HTMLButtonElement>;
  onCancelMute?: MouseEventHandler<HTMLButtonElement>;
  expTempType?: ExpTempType;
} & TExpTemp;

const ExpTemp = (props: ExpTempProps) => {
  const {
    title,
    image,
    video,
    handling,

    isPlay,
    isMute,

    active,
    onClick,
    onPlay,
    onPause,
    onMute,
    onCancelMute,
    onCancelTask,
    expTempType,
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHover, setIsHover] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [blurImgUrl, setBlurImgUrl] = useState<string>();
  const isMobile = useCommonStore(state => state.isMobile);

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (handling) return;

    // 埋点（点击）
    FTAnalytic.addAnalytic({
      type: 'ev',
      action: 'fotor_web$click_up$swap_project_page_features',
      label: {
        button_position: title ? title : "customize",
        project_type: PROJECT_TYPE,
      },
    });

    onClick(e);
  }

  useEffect(() => {
    isPlay ? videoRef.current?.play() : videoRef.current?.pause();
  }, [isPlay]);

  useEffect(() => {
    if (expTempType === ExpTempType.Community) {
      blurImg(image)
        .then(setBlurImgUrl)
        .catch(console.log);
    }
  }, [image, expTempType])

  return (
    <div
      className={styles["exp_temp_wrapper"]}
    >
      <div
        className={clsx(
          styles["exp_temp"],
          active && styles["active"]
        )}
        onClick={handleClick}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <video 
          style={{
            backgroundImage: blurImgUrl ? `url(${blurImgUrl})` : "",
          }}
          ref={videoRef}
          src={video}
          poster={image}
          muted={isMute}
          loop
          playsInline
          preload="none"
          controls={false}
          onContextMenu={e => e.preventDefault()}
        ></video>

        <img 
          style={{
            display: isPlay || active ? "none" : "block"
          }}
          className={styles["poster"]}
          src={image}
          alt=""
          onLoad={() => setLoaded(true)}
        />

        {!handling && !loaded && (
          <div
            className={clsx(
              styles["skeleton"],
              "skeleton_loading"
            )}
          ></div>
        )}

        {isMobile === false && (
          <>
            {isPlay ? (
              <ControlButton
                style={{ left: 8 }}
                disabled={handling}
                onClick={onPause}
              >
                <PauseSvg></PauseSvg>
              </ControlButton>
            ) : (
              <ControlButton
                style={{ left: 8 }}
                disabled={handling}
                onClick={onPlay}
              >
                <PlaySvg></PlaySvg>
              </ControlButton>
            )}

            {/* 播放中或鼠标hover才显示音量键 */}
            {(isPlay || isHover) && (
              <>
                {isMute ? (
                  <ControlButton
                    style={{ right: 8 }}
                    disabled={handling}
                    onClick={onCancelMute}
                  >
                    <MuteSvg></MuteSvg>
                  </ControlButton>
                ) : (
                  <ControlButton
                    style={{ right: 8 }}
                    disabled={handling}
                    onClick={onMute}
                  >
                    <VolumeSvg></VolumeSvg>
                  </ControlButton>
                )}
              </>
            )}
          </>
        )}

        {handling && (
          <div
            className={styles["handling"]}
          >
            <Loading></Loading>

            <button
              className={styles["cancel_task_button"]}
              onClick={onCancelTask}
            >
              <RemoveSvg></RemoveSvg>
            </button>
          </div>
        )}
      </div>

      {title && (
        <p
          className={clsx(
            styles["title"],
            "two_line_ellipsis"
          )}
        >
          {title}
        </p>
      )}
    </div>
  )
}

export default ExpTemp;